// components/Sections/Admin/Matriculas/RegistroManual.jsx
//
// Registrar a mano la matrícula de una persona (clientes antiguos, casos especiales).
// No cobra: queda como registro manual, sin pago asociado.
//
// Cambios respecto a la versión anterior:
//  · Los servicios ya devuelven `data`, pero la pantalla volvía a leer `response.data`:
//    la configuración del recinto quedaba vacía (nunca se veía el valor ni el aviso de
//    "sin configuración"), la verificación fallaba y se asumía "sin matrícula", y la
//    pantalla de éxito mostraba "undefined". El servidor sí rechaza duplicados (409),
//    pero la pantalla decía que todo estaba bien sin saberlo.
//  · La búsqueda se quedaba con el primer resultado si no había coincidencia exacta:
//    se podía matricular a otra persona. Ahora se elige de la lista.
//  · Se confirma antes de registrar y el resultado no se borra solo a los 5 segundos.
import React, { useState, useEffect, useRef } from 'react';
import { Input, Select, Modal, Alert, Spin, DatePicker } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import {
  FiSearch, FiX, FiCheckCircle, FiAlertTriangle, FiUser, FiMapPin, FiFileText, FiClock, FiArrowRight, FiPlus,
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import MatriculaService from '../../../../service/Matriculas';
import RecintoService from '../../../../service/Recintos';
import Usuarios from '../../../../service/Usuarios';
import { useNotify } from '../../../../providers/NotificationProvider';
import { formatearNombre } from '../../../../utils/texto';
import { EncabezadoAdmin, Iniciales, ChipNeutro, claseBtnPrimario, claseBtnSecundario } from '../../../Ui/Admin/PiezasAdmin';
import { ESTADOS, estadoDe } from './estadoMatricula';

const MOTIVO_MIN = 10;
const DEBOUNCE_MS = 350;
const clp = (n) => `$${Math.round(n || 0).toLocaleString('es-CL')}`;
const mensaje = (e, porDefecto) => e?.error || e?.message || porDefecto;

const Paso = ({ n, titulo, listo, children }) => (
  <section className="rounded-3xl bg-white border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,.04)] p-5 sm:p-6">
    <h2 className="flex items-center gap-3 text-base font-extrabold text-gray-900 m-0 mb-4">
      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-extrabold
        ${listo ? 'bg-primary text-white' : 'bg-secondary text-[#00266b]'}`}>
        {listo ? <FiCheckCircle size={16} /> : n}
      </span>
      {titulo}
    </h2>
    {children}
  </section>
);

const RegistroManual = () => {
  const notify = useNotify();

  // Persona
  const [busqueda, setBusqueda] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [persona, setPersona] = useState(null);

  // Recinto
  const [recintos, setRecintos] = useState([]);
  const [recintoId, setRecintoId] = useState(null);
  const [config, setConfig] = useState({ cargando: false, datos: null, falta: false });

  // Verificación: { estado: 'idle'|'cargando'|'ok'|'tiene'|'error', detalle }
  const [verif, setVerif] = useState({ estado: 'idle' });
  const verifId = useRef(0);

  const [motivo, setMotivo] = useState('');
  // Para clientes antiguos: desde cuándo está matriculado y con qué vigencia.
  const [fechaInicio, setFechaInicio] = useState(null);
  const [vigenciaElegida, setVigenciaElegida] = useState(null);
  const [confirmando, setConfirmando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [creada, setCreada] = useState(null);

  const [historial, setHistorial] = useState({ abierto: false, cargando: false, filas: [] });

  useEffect(() => {
    RecintoService.List({ limit: 1000 })
      .then(res => setRecintos((res.data || []).slice().sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))))
      .catch(() => notify.error('Error al cargar recintos'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Búsqueda con espera: por nombre, email o RUT; se elige de la lista.
  useEffect(() => {
    if (persona) return undefined;
    const q = busqueda.trim();
    if (q.length < 2) { setResultados([]); return undefined; }
    let vigente = true;
    const t = setTimeout(async () => {
      setBuscando(true);
      const lista = await Usuarios.buscarUsuarios(q, 8);
      if (vigente) { setResultados(lista); setBuscando(false); }
    }, DEBOUNCE_MS);
    return () => { vigente = false; clearTimeout(t); };
  }, [busqueda, persona]);

  // Configuración del recinto elegido.
  useEffect(() => {
    if (!recintoId) { setConfig({ cargando: false, datos: null, falta: false }); return undefined; }
    let vigente = true;
    setConfig({ cargando: true, datos: null, falta: false });
    // Desde la lista del admin y no desde el endpoint público: ese solo devuelve la
    // matrícula si está a la venta, y la base de un recinto se registra aunque no lo esté.
    MatriculaService.ListarMatriculas({})
      .then(r => {
        if (!vigente) return;
        const c = (r?.data || []).find(x => x.recinto?._id === recintoId);
        setConfig({ cargando: false, datos: c || null, falta: !c });
        setVigenciaElegida(c ? (c.tipoVigencia === 'anual' ? `anual-${c.aniosVigencia || 1}` : 'indefinida') : null);
      })
      .catch(() => { if (vigente) setConfig({ cargando: false, datos: null, falta: false }); });
    return () => { vigente = false; };
  }, [recintoId]);

  // ¿Ya tiene matrícula vigente en ese recinto? Se ignora una respuesta vieja si
  // cambió la persona o el recinto mientras tanto.
  useEffect(() => {
    if (!persona?.rut || !recintoId) { setVerif({ estado: 'idle' }); return; }
    const id = ++verifId.current;
    setVerif({ estado: 'cargando' });
    MatriculaService.VerificarMatriculaUsuario(persona.rut, recintoId)
      .then(r => {
        if (id !== verifId.current) return;
        setVerif(r?.tieneMatriculaVigente ? { estado: 'tiene', detalle: r.matricula } : { estado: 'ok' });
      })
      .catch(e => { if (id === verifId.current) setVerif({ estado: 'error', detalle: mensaje(e, 'No se pudo verificar') }); });
  }, [persona, recintoId]);

  const elegir = (u) => {
    if (!u.rut || !u.email) {
      notify.error('Esa cuenta no tiene RUT o email registrados: complétalos en Usuarios antes de matricular.');
      return;
    }
    setPersona(u);
    setResultados([]);
  };
  const cambiarPersona = () => { setPersona(null); setBusqueda(''); setVerif({ estado: 'idle' }); };

  const verHistorial = async () => {
    setHistorial({ abierto: true, cargando: true, filas: [] });
    try {
      const filas = await MatriculaService.ObtenerHistorialUsuario(persona.rut);
      setHistorial({ abierto: true, cargando: false, filas: Array.isArray(filas) ? filas : [] });
    } catch (e) {
      notify.error(mensaje(e, 'No se pudo cargar el historial'));
      setHistorial({ abierto: true, cargando: false, filas: [] });
    }
  };

  const motivoOk = motivo.trim().length >= MOTIVO_MIN;
  const recintoOk = recintoId && !config.falta && !config.cargando;
  const puedeRegistrar = persona && recintoOk && motivoOk && verif.estado !== 'cargando' && verif.estado !== 'tiene';

  const registrar = async () => {
    setGuardando(true);
    try {
      const [tipoVigencia, anios] = (vigenciaElegida || 'indefinida').split('-');
      const matricula = await MatriculaService.AgregarMatriculaManual({
        rut: persona.rut,
        cognitoId: persona.cognitoId,
        recintoId,
        datosUsuario: { nombre: persona.nombre, email: persona.email, telefono: persona.telefono || '' },
        motivoRegistro: motivo.trim(),
        fechaInicio: fechaInicio ? fechaInicio.hour(12).toISOString() : undefined,
        tipoVigencia,
        aniosVigencia: tipoVigencia === 'anual' ? Number(anios) : undefined,
      });
      setConfirmando(false);
      setCreada({ matricula, persona, recinto: recintos.find(r => r._id === recintoId), config: config.datos, motivo: motivo.trim() });
    } catch (e) {
      // El servidor dice por qué: ya existe (409), recinto sin configuración (404), RUT inválido…
      notify.error(mensaje(e, 'No se pudo registrar la matrícula'));
    } finally {
      setGuardando(false);
    }
  };

  const nuevoRegistro = () => {
    setCreada(null); setPersona(null); setBusqueda(''); setRecintoId(null); setMotivo(''); setVerif({ estado: 'idle' });
    setFechaInicio(null); setVigenciaElegida(null);
  };

  const textoVigencia = (v) => {
    if (!v) return '—';
    if (v === 'indefinida') return 'De por vida';
    const n = Number(v.split('-')[1]) || 1;
    return n === 1 ? '1 año' : `${n} años`;
  };

  const recinto = recintos.find(r => r._id === recintoId);

  // ── Resultado ──
  if (creada) {
    const m = creada.matricula || {};
    return (
      <div className="max-w-3xl">
        <EncabezadoAdmin titulo="Registro" destacado="manual" subtitulo="Matrícula registrada sin cobro" />
        <section className="rounded-3xl bg-white border border-gray-100 overflow-hidden">
          <div className="h-1.5 bg-secondary" aria-hidden />
          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center"><FiCheckCircle size={22} /></span>
              <div>
                <p className="text-xl font-extrabold text-gray-900 m-0">Matrícula registrada</p>
                <p className="text-sm text-gray-500 m-0">{formatearNombre(creada.persona.nombre)} ya tiene matrícula en {formatearNombre(creada.recinto?.nombre)}.</p>
              </div>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 m-0">
              {[
                ['Persona', `${formatearNombre(creada.persona.nombre)} · ${creada.persona.rut}`],
                ['Recinto', formatearNombre(creada.recinto?.nombre) || '—'],
                ['Vigencia', m.fechaVencimiento
                  ? `${m.aniosVigencia > 1 ? `${m.aniosVigencia} años` : '1 año'} · vence ${dayjs(m.fechaVencimiento).format('DD/MM/YYYY')}${m.estado === 'vencida' ? ' (ya vencida)' : ''}`
                  : 'De por vida'],
                ['Registro', 'Manual (sin pago asociado)'],
              ].map(([k, v]) => (
                <div key={k} className="p-3.5 rounded-2xl bg-gray-50">
                  <dt className="text-[11px] font-bold uppercase tracking-wider text-gray-500">{k}</dt>
                  <dd className="m-0 mt-0.5 text-sm font-semibold text-gray-900">{v}</dd>
                </div>
              ))}
              <div className="p-3.5 rounded-2xl bg-gray-50 sm:col-span-2">
                <dt className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Motivo</dt>
                <dd className="m-0 mt-0.5 text-sm text-gray-800">{creada.motivo}</dd>
              </div>
            </dl>
            <div className="flex flex-wrap gap-2 mt-6">
              <button type="button" onClick={nuevoRegistro} className={claseBtnPrimario}><FiPlus size={15} /> Registrar otra</button>
              <Link to="/admin/matriculas/usuarios" className={claseBtnSecundario}>Ver usuarios matriculados <FiArrowRight size={14} /></Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="max-w-[1100px]">
      <EncabezadoAdmin titulo="Registro" destacado="manual"
        subtitulo="Matricular a una persona sin pago online (clientes antiguos o casos especiales)" />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5 items-start">
        <div className="space-y-5">
          {/* ── 1. Persona ── */}
          <Paso n={1} titulo="Persona" listo={!!persona}>
            {persona ? (
              <div className="flex flex-wrap items-center gap-3 p-3.5 rounded-2xl bg-blue-50/60 border border-blue-100">
                <Iniciales nombre={persona.nombre} className="!w-11 !h-11 !bg-white" />
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-gray-900 m-0 truncate">{formatearNombre(persona.nombre)}</p>
                  <p className="text-xs text-gray-600 m-0 truncate"><span className="font-mono">{persona.rut}</span> · {persona.email}{persona.telefono && ` · ${persona.telefono}`}</p>
                </div>
                <button type="button" onClick={verHistorial} className="text-xs font-bold text-primary hover:underline">Historial</button>
                <button type="button" onClick={cambiarPersona} className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-900">
                  <FiX size={13} /> Cambiar
                </button>
              </div>
            ) : (
              <div>
                <Input size="large" allowClear prefix={<FiSearch className="text-gray-400" />}
                  placeholder="Nombre, email o RUT…" value={busqueda} onChange={e => setBusqueda(e.target.value)}
                  suffix={buscando ? <LoadingOutlined /> : null} aria-label="Buscar persona" className="!rounded-2xl" autoFocus />
                {busqueda.trim().length >= 2 && !buscando && (
                  resultados.length ? (
                    <ul className="m-0 mt-2 p-1 list-none rounded-2xl border border-gray-100 divide-y divide-gray-50">
                      {resultados.map(u => (
                        <li key={u.cognitoId || u.email}>
                          <button type="button" onClick={() => elegir(u)}
                            className="w-full flex items-center gap-3 p-2.5 rounded-xl text-left hover:bg-[#fffbea] transition-colors">
                            <Iniciales nombre={u.nombre} />
                            <span className="min-w-0 flex-1">
                              <span className="block text-sm font-bold text-gray-900 truncate">{formatearNombre(u.nombre)}</span>
                              <span className="block text-xs text-gray-500 truncate">
                                {u.rut ? <span className="font-mono">{u.rut}</span> : <span className="text-red-600">sin RUT</span>} · {u.email}
                              </span>
                            </span>
                            <FiArrowRight size={14} className="text-gray-300" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500 mt-2 mb-0">No encontramos cuentas con «{busqueda.trim()}». La persona necesita una cuenta en la plataforma.</p>
                  )
                )}
              </div>
            )}
          </Paso>

          {/* ── 2. Recinto ── */}
          <Paso n={2} titulo="Recinto" listo={!!recintoOk && !!config.datos}>
            <Select size="large" className="w-full" placeholder="Selecciona el recinto" value={recintoId}
              onChange={setRecintoId} showSearch optionFilterProp="label" aria-label="Recinto"
              options={recintos.map(r => ({ value: r._id, label: formatearNombre(r.nombre) }))} />
            {config.cargando && <p className="text-sm text-gray-500 mt-3 mb-0"><Spin size="small" /> Revisando la configuración…</p>}
            {config.falta && (
              <Alert className="mt-3" type="warning" showIcon message="Este recinto no tiene matrícula configurada"
                description={<>Configúrala primero en <Link to="/admin/matriculas/configuracion" className="font-bold">Configuración de matrículas</Link>.</>} />
            )}
            {config.datos && (
              <>
                <div className="flex flex-wrap items-center gap-2 mt-3 text-sm text-gray-600">
                  <ChipNeutro>{config.datos.activa ? 'En venta' : 'No se vende'}</ChipNeutro>
                  <span>Valor de venta {config.datos.valorMatricula > 0 ? clp(config.datos.valorMatricula) : 'gratis'} · <strong>aquí no se cobra</strong></span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <label className="text-xs font-bold text-gray-700">
                    Fecha de matrícula
                    <DatePicker className="w-full mt-1" format="DD/MM/YYYY" value={fechaInicio} onChange={setFechaInicio}
                      placeholder="Hoy" disabledDate={(d) => d && d.isAfter(dayjs(), 'day')} />
                    <span className="block font-normal text-gray-500 mt-1">Desde ahí corre la vigencia. Vacío = hoy.</span>
                  </label>
                  <label className="text-xs font-bold text-gray-700">
                    Vigencia
                    <Select className="w-full mt-1" value={vigenciaElegida} onChange={setVigenciaElegida} options={[
                      { value: 'anual-1', label: '1 año' }, { value: 'anual-2', label: '2 años' },
                      { value: 'anual-3', label: '3 años' }, { value: 'indefinida', label: 'De por vida' },
                    ]} />
                  </label>
                </div>
              </>
            )}

            {/* Verificación */}
            {verif.estado === 'cargando' && (
              <p className="flex items-center gap-2 text-sm text-gray-500 mt-3 mb-0"><LoadingOutlined /> Revisando si ya tiene matrícula…</p>
            )}
            {verif.estado === 'ok' && (
              <p className="flex items-center gap-2 text-sm text-primary font-semibold mt-3 mb-0"><FiCheckCircle size={15} /> No tiene matrícula vigente en este recinto.</p>
            )}
            {verif.estado === 'tiene' && (
              <Alert className="mt-3" type="error" showIcon icon={<FiAlertTriangle />}
                message="Ya tiene una matrícula vigente en este recinto"
                description={verif.detalle?.fechaVencimiento
                  ? `Vence el ${dayjs(verif.detalle.fechaVencimiento).format('DD/MM/YYYY')}.`
                  : 'Es de vigencia indefinida.'} />
            )}
            {verif.estado === 'error' && (
              <Alert className="mt-3" type="warning" showIcon message="No se pudo verificar si ya tiene matrícula"
                description={`${verif.detalle}. Puedes continuar: si ya tiene una, el servidor rechazará el registro.`} />
            )}
          </Paso>

          {/* ── 3. Motivo ── */}
          <Paso n={3} titulo="Motivo" listo={motivoOk}>
            <Input.TextArea value={motivo} onChange={e => setMotivo(e.target.value)} autoSize={{ minRows: 3, maxRows: 6 }}
              maxLength={500} showCount aria-label="Motivo del registro manual"
              placeholder="Ej: cliente antiguo con matrícula pagada en efectivo en 2025 (boleta 1234)."
              status={motivo.length > 0 && !motivoOk ? 'error' : ''} />
            <p className={`text-xs mt-1.5 mb-0 ${motivo.length > 0 && !motivoOk ? 'text-red-600' : 'text-gray-500'}`}>
              Mínimo {MOTIVO_MIN} caracteres. Queda guardado junto a la matrícula.
            </p>
          </Paso>
        </div>

        {/* ── Resumen ── */}
        <aside className="lg:sticky lg:top-4 rounded-3xl bg-[#00266b] text-white p-5">
          <p className="text-xs font-bold uppercase tracking-widest text-secondary m-0 mb-4">Resumen</p>
          <ul className="m-0 p-0 list-none space-y-3 text-sm">
            <li className="flex items-start gap-2.5"><FiUser className="mt-0.5 text-blue-200 flex-shrink-0" />
              <span>{persona ? formatearNombre(persona.nombre) : <span className="text-blue-200">Falta elegir la persona</span>}</span></li>
            <li className="flex items-start gap-2.5"><FiMapPin className="mt-0.5 text-blue-200 flex-shrink-0" />
              <span>{recinto ? formatearNombre(recinto.nombre) : <span className="text-blue-200">Falta elegir el recinto</span>}</span></li>
            <li className="flex items-start gap-2.5"><FiClock className="mt-0.5 text-blue-200 flex-shrink-0" />
              <span>{config.datos ? `${textoVigencia(vigenciaElegida)}${fechaInicio ? ` desde el ${fechaInicio.format('DD/MM/YYYY')}` : ''}` : <span className="text-blue-200">—</span>}</span></li>
            <li className="flex items-start gap-2.5"><FiFileText className="mt-0.5 text-blue-200 flex-shrink-0" />
              <span>{motivoOk ? 'Motivo listo' : <span className="text-blue-200">Falta el motivo</span>}</span></li>
          </ul>
          <button type="button" onClick={() => setConfirmando(true)} disabled={!puedeRegistrar}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 py-3 rounded-full bg-secondary text-[#00266b] text-sm font-extrabold hover:brightness-105 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
            Registrar matrícula
          </button>
          <p className="text-[11px] text-blue-200 mt-3 mb-0 text-center">Sin cobro · queda como registro manual</p>
        </aside>
      </div>

      {/* ── Confirmación ── */}
      <Modal open={confirmando} onCancel={() => !guardando && setConfirmando(false)} title="¿Registrar esta matrícula?"
        okText="Sí, registrar" cancelText="Revisar" onOk={registrar} okButtonProps={{ loading: guardando }} width={480}>
        {persona && recinto && (
          <div className="space-y-2 text-sm">
            <p className="m-0"><strong>{formatearNombre(persona.nombre)}</strong> <span className="text-gray-500">({persona.rut})</span></p>
            <p className="m-0">Recinto: <strong>{formatearNombre(recinto.nombre)}</strong></p>
            <p className="m-0">Vigencia: <strong>{textoVigencia(vigenciaElegida).toLowerCase()}</strong>{fechaInicio ? ` desde el ${fechaInicio.format('DD/MM/YYYY')}` : ' desde hoy'}</p>
            <p className="m-0 text-gray-600">No se cobra: quedará como registro manual, sin pago asociado.</p>
          </div>
        )}
      </Modal>

      {/* ── Historial ── */}
      <Modal open={historial.abierto} onCancel={() => setHistorial(h => ({ ...h, abierto: false }))} footer={null} width={640}
        title={persona && <>Matrículas de <span className="hl-amarillo">{formatearNombre(persona.nombre)}</span></>}>
        {historial.cargando ? <div className="py-10 text-center"><Spin /></div>
          : historial.filas.length === 0 ? <p className="py-8 text-center text-sm text-gray-500 m-0">No tiene matrículas registradas.</p>
            : (
              <ul className="m-0 p-0 list-none space-y-2">
                {historial.filas.map(h => {
                  const e = ESTADOS[estadoDe(h)];
                  return (
                    <li key={h._id} className="flex items-center justify-between gap-3 p-3 rounded-2xl border border-gray-100">
                      <span className="min-w-0">
                        <span className="block text-sm font-bold text-gray-900 truncate">{formatearNombre(h.recinto?.nombre) || 'Sin recinto'}</span>
                        <span className="block text-xs text-gray-500">Desde {h.fechaInicio ? dayjs(h.fechaInicio).format('DD/MM/YYYY') : '—'}</span>
                      </span>
                      <span className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-bold ${e.cls}`}>{e.label}</span>
                    </li>
                  );
                })}
              </ul>
            )}
      </Modal>
    </div>
  );
};

export default RegistroManual;
